import {
    faCheckCircle,
    faIdCard,
    faUser,
    faFileAlt,
    faMedal,
    faGlobe,
    faEnvelope,
    faUserTie
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Badge, Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Banderas from '../cliente/Banderas';
import ModalSeguimientoLead from '../leads/ModalSeguimientoLead';

/**
 * Componente para mostrar un distribuidor individual como tarjeta
 */
const DistribuidorCard = ({ distribuidor, isSelected = false, onToggleSelection }) => {
    const url = `/clientes/detalle/${distribuidor.idClienteAkasia}`;
    const [showModalSeguimientoLead, setShowModalSeguimientoLead] = useState(false);
    const [idLead, setIdLead] = useState(0);

    // Parsear los leads vinculados
    const leadsArray = distribuidor.vLeadsVinculados 
        ? distribuidor.vLeadsVinculados.split(',').map(id => id.trim()).filter(id => id)
        : [];

    const handleCardClick = (e) => {
        // No activar selección si se hace clic en un botón o enlace
        if (e.target.tagName === 'A' || e.target.closest('a') || e.target.closest('button')) {
            return;
        }

        if (onToggleSelection) {
            onToggleSelection(distribuidor.idClienteAkasia);
        }
    };

    const handleLeadClick = (e, leadId) => {
        e.stopPropagation();
        setIdLead(parseInt(leadId));
        setShowModalSeguimientoLead(true);
    };

    // Determinar clases CSS según estado
    const cardClasses = [
        'mb-3',
        'shadow-sm',
        'border-warning',
        isSelected ? 'border-primary border-3' : ''
    ].filter(Boolean).join(' ');

    // Estilos dinámicos para el fondo
    const cardStyle = {
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        ...(isSelected && {
            backgroundColor: 'rgba(13, 110, 253, 0.1)',
            boxShadow: '0 0 0 0.2rem rgba(13, 110, 253, 0.25)'
        })
    };

    // Documentos disponibles
    const documentos = [
        { 
            nombre: 'Identificación', 
            archivo: distribuidor.vIdFotoDoc, 
            icon: faIdCard,
            color: 'primary',
            badge: 'I'
        },
        { 
            nombre: 'Cédula Fiscal', 
            archivo: distribuidor.vCedulaFiscalDoc,
            icon: faFileAlt,
            color: 'info',
            badge: 'C'
        },
        { 
            nombre: 'Comprobante Domicilio', 
            archivo: distribuidor.vComprobanteDomicilioDoc,
            icon: faFileAlt,
            color: 'success',
            badge: 'D'
        },
        { 
            nombre: 'Motivos', 
            archivo: distribuidor.vMotivosDoc,
            icon: faFileAlt,
            color: 'warning',
            badge: 'M'
        }
    ];

    return (
        <Card
            className={cardClasses}
            onClick={handleCardClick}
            style={cardStyle}
        >
            <Card.Body>
                <Row className="align-items-center">
                    {/* ID, Badge Distribuidor y Banderas */}
                    <Col xs={12} md={2} className="mb-2 mb-md-0">
                        <div className="d-flex flex-column align-items-start">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                {isSelected && (
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        className="text-primary"
                                        size="lg"
                                    />
                                )}
                                <Link to={url} className="text-decoration-none">
                                    <span className="fw-bold" style={{ fontSize: '1.1rem', color: '#2c7be5' }}>
                                        #{distribuidor.idClienteAkasia}
                                    </span>
                                </Link>
                            </div>

                            {/* Badge Distribuidor Autorizado */}
                            <Badge bg="warning" text="dark" className="mb-2 fs--2">
                                <FontAwesomeIcon icon={faMedal} className="me-1" />
                                Distribuidor
                            </Badge>
                           
                            {/* Bandera del país */}
                            <div className="mb-2">
                                <Banderas Height={32} vPais={distribuidor.vPais} />
                            </div>

                            <small className="text-muted">{distribuidor.joinDate}</small>
                            


                            {/* Agente */}
                            {distribuidor.vAgente && (
                                <>
                                    <hr className="my-2 w-100" />
                                    <div className="small text-muted">
                                        <strong>Agente:</strong>
                                        <div className="text-truncate" style={{ maxWidth: '120px' }}>
                                            {distribuidor.vAgente}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </Col>

                    {/* Información principal del distribuidor */}
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                        <Row>
                            {/* Columna 1: Datos del Distribuidor */}
                            <Col xs={12} lg={6} className="mb-2 mb-lg-0">
                                <div className="mb-2">
                                    <h6 className="mb-2">
                                        <Link to={url} className="text-decoration-none">
                                            <FontAwesomeIcon icon={faUser} className="me-2 text-warning" />
                                            <span className="text-truncate d-inline-block" style={{ maxWidth: '250px' }}>
                                                {distribuidor.vNombreDistribuidor}
                                            </span>
                                        </Link>
                                    </h6>

                                    <div className="text-muted small mb-1">
                                        <strong className="text-primary">Datos del Distribuidor</strong>
                                    </div>

                                    {/* Teléfono */}
                                    {distribuidor.vTelefonoPersonal && (
                                        <div className="small mb-1">
                                            <a href={`https://wa.me/${distribuidor.vTelefonoPersonal}`} target="_blank" rel="noreferrer" className="text-decoration-none">
                                                <img src="/img/whatsapp20.png" alt="WhatsApp" className="me-1" />
                                                {distribuidor.vTelefonoPersonal}
                                            </a>
                                        </div>
                                    )}

                                    {/* Email */}
                                    {distribuidor.vEmail && (
                                        <div className="small mb-1 text-muted">
                                            <FontAwesomeIcon icon={faEnvelope} className="me-1 text-success" />
                                            <a href={`mailto:${distribuidor.vEmail}`} className="text-decoration-none">
                                                <span className="d-inline-block text-truncate" style={{ maxWidth: '200px' }}>
                                                    {distribuidor.vEmail}
                                                </span>
                                            </a>
                                        </div>
                                    )}

                                    {/* País */}
                                    <div className="small text-muted mb-1">
                                        <strong>País:</strong> {distribuidor.vPais}
                                    </div>

                                    {/* Estado/Departamento/Provincia */}
                                    {distribuidor.vEstadoDepProv && (
                                        <div className="small text-muted">
                                            <strong>Estado:</strong> {distribuidor.vEstadoDepProv}
                                        </div>
                                    )}
                                </div>
                            </Col>

                            {/* Columna 2: Datos Fiscales */}
                            <Col xs={12} lg={6}>
                                <div className="mb-2">
                                    <div className="text-muted small mb-2">
                                        <strong className="text-info">Datos Fiscales</strong>
                                    </div>

                                    {/* Razón Social */}
                                    {distribuidor.vRazonSocial && (
                                        <div className="small mb-1 text-muted">
                                            <FontAwesomeIcon icon={faIdCard} className="me-1 text-info" />
                                            <strong>Razón Social:</strong>
                                            <div className="text-truncate" style={{ maxWidth: '200px' }}>
                                                {distribuidor.vRazonSocial}
                                            </div>
                                        </div>
                                    )}

                                    {/* RFC */}
                                    <div className="small mb-1 text-muted">
                                        <FontAwesomeIcon icon={faIdCard} className="me-1 text-info" />
                                        <strong>RFC:</strong> {distribuidor.vRfc || 'N/A'}
                                    </div>

                                    {/* Número de Registro Tributario */}
                                    {distribuidor.vNumRegTrib && (
                                        <div className="small mb-1 text-muted">
                                            <FontAwesomeIcon icon={faIdCard} className="me-1 text-info" />
                                            <strong>Núm. Reg. Trib.:</strong> {distribuidor.vNumRegTrib}
                                        </div>
                                    )}

                                    {/* País de Facturación */}
                                    {distribuidor.vPaisFacturacion && (
                                        <div className="small mb-1 text-muted">
                                            <FontAwesomeIcon icon={faGlobe} className="me-1 text-warning" />
                                            <strong>País Fact.:</strong> {distribuidor.vPaisFacturacion}
                                        </div>
                                    )}

                                    {/* Documentos adjuntos */}
                                    <hr className="my-2" />
                                    <div className="text-muted small mb-2">
                                        <strong className="text-success">Documentos</strong>
                                    </div>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {documentos.map((doc, index) => (
                                            doc.archivo ? (
                                                <OverlayTrigger
                                                    key={index}
                                                    placement="top"
                                                    overlay={<Tooltip>{doc.nombre}</Tooltip>}
                                                >
                                                    <Link
                                                        to={`/api/distribuidor/OnGetFile/?idClienteAkasia=${distribuidor.idClienteAkasia}&vNombreArchivo=${doc.archivo}`}
                                                        target="_blank"
                                                        className="text-decoration-none"
                                                    >
                                                        <Badge bg={doc.color} className="fs--2">
                                                            {doc.badge}
                                                        </Badge>
                                                    </Link>
                                                </OverlayTrigger>
                                            ) : null
                                        ))}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/* Información de ventas y estadísticas */}
                    <Col xs={12} md={4} className="text-md-end">
                        <div className="mb-3">
                            <div className="text-muted small mb-2">
                                <strong className="text-success">Estadísticas</strong>
                            </div>

                            {/* Número de Licencias */}
                            <div className="mb-2 d-flex justify-content-end align-items-center">
                                <span className="small text-muted me-2">
                                    <strong>Licencias:</strong>
                                </span>
                                <span className={`fw-bold ${distribuidor.iNL > 0 ? 'text-success' : 'text-secondary'}`} style={{ fontSize: '1.2rem' }}>
                                    {distribuidor.iNL}
                                </span>
                            </div>

                            {/* Leads Vinculados */}
                            {leadsArray.length > 0 && (
                                <div className="mb-2">
                                    <div className="d-flex justify-content-end align-items-center mb-1">
                                        <span className="small text-muted me-2">
                                            <FontAwesomeIcon icon={faUserTie} className="me-1 text-info" />
                                            <strong>Leads Vinculados:</strong>
                                        </span>
                                    </div>
                                    <div className="d-flex justify-content-end gap-1 flex-wrap">
                                        {leadsArray.map((leadId, index) => (
                                            <OverlayTrigger
                                                key={index}
                                                placement="top"
                                                overlay={<Tooltip>Ver Lead #{leadId}</Tooltip>}
                                            >
                                                <Badge
                                                    bg="info"
                                                    className="fs--2"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={(e) => handleLeadClick(e, leadId)}
                                                >
                                                    #{leadId}
                                                </Badge>
                                            </OverlayTrigger>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Monto vendido */}
                            {distribuidor.dMontoVendido !== undefined && distribuidor.dMontoVendido !== null && (
                                <>
                                    <hr className="my-2" />
                                    <div className="text-end">
                                        <div className="small text-muted mb-1">
                                            <strong>Total Compras:</strong>
                                        </div>
                                        <div className="fw-bold text-warning" style={{ fontSize: '1.1rem' }}>
                                            ${parseFloat(distribuidor.dMontoVendido).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="small text-muted">MXN</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Ver detalles link */}
                        <div className="mt-3">
                            <Link to={url} className="btn btn-sm btn-outline-warning">
                                <img src="/img/ver20.png" alt="Ver" className="me-1" />
                                Ver detalles
                            </Link>
                        </div>
                    </Col>
                </Row>
            </Card.Body>

            <ModalSeguimientoLead
                showModal={showModalSeguimientoLead}
                setShowModal={setShowModalSeguimientoLead}
                idLead={idLead}
                setIdLead={setIdLead}
            />
        </Card>
    );
};

DistribuidorCard.propTypes = {
    distribuidor: PropTypes.shape({
        idClienteAkasia: PropTypes.number.isRequired,
        vRfc: PropTypes.string,
        vRazonSocial: PropTypes.string,
        vNumRegTrib: PropTypes.string,
        vNombreDistribuidor: PropTypes.string.isRequired,
        vAgente: PropTypes.string,
        vEmail: PropTypes.string,
        vTelefonoPersonal: PropTypes.string,
        vPais: PropTypes.string.isRequired,
        vPaisFacturacion: PropTypes.string,
        vEstadoDepProv: PropTypes.string,
        iNL: PropTypes.number,
        dMontoVendido: PropTypes.number,
        vIdFotoDoc: PropTypes.string,
        vCedulaFiscalDoc: PropTypes.string,
        vComprobanteDomicilioDoc: PropTypes.string,
        vMotivosDoc: PropTypes.string,
        joinDate: PropTypes.string.isRequired,
        vLeadsVinculados: PropTypes.string
    }).isRequired,
    isSelected: PropTypes.bool,
    onToggleSelection: PropTypes.func
};

export default DistribuidorCard;
